/// Auth + app-wide configuration. Vite's `import.meta.env` carries
/// `VITE_*` build-time vars from `.env.local`; sensible defaults
/// here so the local dev experience works without a config file.
///
/// To point at staging:
///   .env.local
///     VITE_AUTH_BASE_URL=https://goastern-auth-staging.<acct>.workers.dev
///
/// To point at production (the default):
///   no override needed; runtime uses `https://auth.goastern.com`.

import { Capacitor } from "@capacitor/core";

import type { AuthClientOptions } from "@nav-games/auth-client";

const env = import.meta.env as Record<string, string | undefined>;

export const AUTH_BASE_URL =
  env.VITE_AUTH_BASE_URL ?? "https://auth.goastern.com";

/// nav-games's per-app worker. Owns the entitlements table + IAP
/// receipt validation. The SPA polls `/me/entitlements` on every
/// sign-in to learn whether the user has unlocked the premium tier.
export const WORKER_BASE_URL =
  env.VITE_WORKER_BASE_URL ?? "https://nav-games-api.goastern.com";

/// Where goastern-auth's magic-link redeem / Apple SIWA / Google OAuth
/// redirect on success. Must match an entry in the worker's
/// `WEB_RETURN_URL_ALLOWLIST` env var for the `nav` app, or
/// `/auth/magic/request` will 400.
///
/// We deliberately target the SPA *root* rather than `/auth/callback`:
/// Capacitor's local-server has no SPA fallback (unlike Cloudflare
/// Pages' `_redirects`), so a redirect to `https://localhost/auth/callback`
/// 404s inside the Android WebView and the React app never loads. The
/// root URL works on every surface — Cloudflare Pages, Capacitor
/// iOS/Android, dev server — and `App.tsx` consumes the URL fragment
/// on mount regardless of path.
///
/// On Capacitor we use a **custom URI scheme** (`goastern-nav://auth/complete`)
/// rather than `https://localhost/...`. The standard mobile-OAuth
/// handoff pattern. Android's WebView won't reliably intercept a
/// cross-origin redirect to `https://localhost/` (the asset-loader
/// only routes requests originating from inside the local app), so
/// the WebView shows "this site can't be reached" when the auth
/// worker tries to bounce back via https.
///
/// Custom scheme works around that: Android's intent-filter on the
/// MainActivity catches `goastern-nav://`, the Capacitor bridge
/// fires `appUrlOpen`, and `App.tsx` reads the fragment and consumes
/// the session.
///
/// This URL **must** be allowlisted on the auth worker via
/// `WEB_RETURN_URL_ALLOWLIST.nav`, exact string match.
export const RETURN_URL = Capacitor.isNativePlatform()
  ? "goastern-nav://auth/complete"
  : `${window.location.origin}/`;

export const AUTH_CLIENT_OPTIONS: AuthClientOptions = {
  authBaseUrl: AUTH_BASE_URL,
  app: "nav",
  platform: "web",
  returnUrl: RETURN_URL,
};
